import flow from "lodash/fp/flow";
import negate from "lodash/fp/negate";
import join from "lodash/fp/join";
import omit from "lodash/fp/omit";
import mapValues from "lodash/fp/mapValues";
import mapKeys from "lodash/mapKeys";
import isNull from "lodash/fp/isNull";
import pickBy from "lodash/fp/pickBy";
import isUndefined from "lodash/fp/isUndefined";

const ignoreValuesFor = [
  "full-right-hand",
  "set-colors",
  "toe-alignment",
  "t-trap",
  "t-trap-reinforced",
  "pocket-lacing-type",
  "pocket-lace--glove",
  "t-spine-lacing-type",
  "t-spine-lace--glove",
  "internal-fit",
  "pad-size",
  "thigh-rise",
  "personalizedName",
  "noPersonalization",
  "center-channel",
  "knee-block-and-calf-plate",
  "top-welting",
  "bottom-welting",
  "back-cover",
  "binding",
  "elastic-and-velcro",
  "construction-lace",
  "top-spear",
  "bottom-spear",
  "calf-wrap",
  "knee-block-individual-color",
  "calf-plate-individual-color",
  "outside-zone-color",
  "outside-zone-color--glove",
  "outside-zone-color--blocker",
  "outside-bottom-zone-color",
  "outside-bottom-zone-color--glove",
  "outside-bottom-zone-color--blocker",
  "top-zone-color",
  "hyp2rlite-logo-color",
];

const valueOverrides = {
  "pad-construction": {
    "vapor hyperlite": "vapor-flylite",
  },
  "glove-construction": {
    "vapor hyperlite2": "vapor-flylite",
    "Vapor Hyperlite2": "vapor-flylite",
  },
  "blocker-construction": {
    "vapor hyperlite2": "vapor-flylite",
    "Vapor Hyperlite2": "vapor-flylite",
    "Vapor 2PC": "flylite-2pc",
  },
  graphic: {
    "vapor hyperlite": "vapor-flylite",
  },
};

const handleFullSetColors = (userChoiceValues) =>
  mapKeys(userChoiceValues, (value, key) =>
    key.endsWith("-color") ? key + "--pads" : key
  );

export default function getGoalieGearAddToOrderValues({
  userChoiceValues,
  product,
  materialID,
  state,
}) {
  const addRequestedNamingConvention = (userChoices) => ({
    ...userChoices,
    graphic:
      valueOverrides["graphic"][userChoiceValues["graphic"]] ||
      userChoiceValues["graphic"] ||
      null,
    "pad-construction":
      valueOverrides["pad-construction"][
        userChoiceValues["pad-construction"]
      ] ||
      userChoiceValues["pad-construction"] ||
      null,
    "glove-construction":
      valueOverrides["glove-construction"][
        userChoiceValues["glove-construction"]
      ] ||
      userChoiceValues["glove-construction"] ||
      null,
    "blocker-construction":
      valueOverrides["blocker-construction"][
        userChoiceValues["blocker-construction"]
      ] ||
      userChoiceValues["blocker-construction"] ||
      null,
    handedness: userChoiceValues["full-right-hand"]
      ? "full right"
      : "regular" || null,
    "toe-bridge-combined": userChoiceValues["toe-alignment"]
      ? join("-")([
          userChoiceValues["toe-bridge"],
          userChoiceValues["toe-alignment"],
        ])
      : userChoiceValues["toe-attachment"] || null,
    "pocket-t-trap": userChoiceValues["t-trap-reinforced"]
      ? join("-")([
          userChoiceValues["t-trap"],
          userChoiceValues["t-trap-reinforced"],
        ])
      : userChoiceValues["t-trap"] || null,
    "pocket-lacing": userChoiceValues["pocket-lacing-type"] || null,
    "pocket-lacing-combined":
      join("-")([
        userChoiceValues["pocket-lacing-type"],
        userChoiceValues["pocket-lace--glove"],
      ]) || null,
    "t-spine-lacing":
      userChoiceValues["t-trap"] === "Single"
        ? "N/A"
        : userChoiceValues["t-spine-lacing-type"] || null,
    "t-spine-lacing-combined":
      userChoiceValues["t-trap"] === "Single"
        ? "N/A"
        : join("-")([
            userChoiceValues["t-spine-lacing-type"],
            userChoiceValues["t-spine-lace--glove"],
          ]) || null,
    "hand-size": userChoiceValues["internal-fit"] || null,
    "size-value": userChoiceValues["pad-size"] || null,
    size: userChoiceValues["thigh-rise"]
      ? userChoiceValues["pad-size"] + userChoiceValues["thigh-rise"]
      : userChoiceValues["pad-size"] || null,
    "personalized-name": userChoiceValues["personalizedName"] || null,
    "knee-calf-plate-color":
      userChoiceValues["knee-block-and-calf-plate"] || null, // ProCustom/Custom only
    "knee-block-color":
      userChoiceValues["knee-block-and-calf-plate"] ||
      userChoiceValues["knee-block-individual-color"] || // Digi-Print
      null,
    "calf-plate-color":
      userChoiceValues["knee-block-and-calf-plate"] ||
      userChoiceValues["calf-plate-individual-color"] || // Digi-Print
      null,
    "no-personalization": userChoiceValues["noPersonalization"] || null,
    "center-channel-color": userChoiceValues["center-channel"] || null,
    "top-welting-color": userChoiceValues["top-welting"] || null,
    "bottom-welting-color": userChoiceValues["bottom-welting"] || null,
    "calf-wrap-color": userChoiceValues["calf-wrap"] || null,
    "back-cover-color": userChoiceValues["back-cover"] || null,
    "binding-color": userChoiceValues["binding"] || null,
    "elastic-and-velcro-color": userChoiceValues["elastic-and-velcro"] || null,
    "construction-lace-color": userChoiceValues["construction-lace"] || null,
    "top-spear-color": userChoiceValues["top-spear"] || null,
    "bottom-spear-color": userChoiceValues["bottom-spear"] || null,
    "outside-color": userChoiceValues["outside-zone-color"] || null,
    "outside-bottom-color":
      userChoiceValues["outside-bottom-zone-color"] ||
      userChoiceValues["outside-bottom-color"] ||
      null,
    "top-color": userChoiceValues["top-zone-color"] || null,
    "flylite-logo-color": userChoiceValues["hyp2rlite-logo-color"] || null,
  });

  const gloveAndBlockerColorChoices = {
    "middle-zone-color--glove":
      userChoiceValues["middle-zone-color--glove"] ||
      userChoiceValues["middle-zone-color"] ||
      null,
    "inside-zone-color--glove":
      userChoiceValues["inside-zone-color--glove"] ||
      userChoiceValues["inside-zone-color"] ||
      null,
    "inside-middle-color--glove":
      userChoiceValues["inside-middle-color--glove"] ||
      userChoiceValues["inside-middle-color"] ||
      null,
    "inside-middle-accent-color--glove":
      userChoiceValues["inside-middle-accent-color--glove"] ||
      userChoiceValues["inside-middle-accent-color"] ||
      null,
    "outside-color--glove":
      userChoiceValues["outside-zone-color--glove"] ||
      userChoiceValues["outside-zone-color"] ||
      null,
    "top-color--glove":
      userChoiceValues["top-zone-color--glove"] ||
      userChoiceValues["top-zone-color"] ||
      null,

    "outside-bottom-color--glove":
      userChoiceValues["outside-bottom-color--glove"] ||
      userChoiceValues["outside-bottom-zone-color--glove"] ||
      userChoiceValues["outside-bottom-zone-color"] ||
      userChoiceValues["outside-bottom-color"] ||
      null,
    // Shadow
    "outside-top-color--glove":
      userChoiceValues["outside-top-color--glove"] ||
      userChoiceValues["outside-top-color"] ||
      null,
    "inside-top-color--glove":
      userChoiceValues["inside-top-color--glove"] ||
      userChoiceValues["inside-top-color"] ||
      null,
    "inside-bottom-color--glove":
      userChoiceValues["inside-bottom-color--glove"] ||
      userChoiceValues["inside-bottom-color"] ||
      null,
    "shadow-background-color--glove":
      userChoiceValues["shadow-background-color--glove"] ||
      userChoiceValues["shadow-background-color"] ||
      null,
    "shadow-logo-fill-color--glove":
      userChoiceValues["shadow-logo-fill-color--glove"] ||
      userChoiceValues["shadow-logo-fill-color"] ||
      null,
    "shadow-logo-outline-color--glove":
      userChoiceValues["shadow-logo-outline-color--glove"] ||
      userChoiceValues["shadow-logo-outline-color"] ||
      null,

    "line-zone-color--glove":
      userChoiceValues["line-zone-color--glove"] ||
      userChoiceValues["line-zone-color"] ||
      null,
    "center-zone-color--glove":
      userChoiceValues["center-zone-color--glove"] ||
      userChoiceValues["center-zone-color"] ||
      null,
    "top-spear-color--glove":
      userChoiceValues["top-spear--glove"] ||
      userChoiceValues["top-spear"] ||
      null,
    "bottom-spear-color--glove":
      userChoiceValues["bottom-spear--glove"] ||
      userChoiceValues["bottom-spear"] ||
      null,
    "spear-color--glove":
      userChoiceValues["spear-color--glove"] ||
      userChoiceValues["spear-color"] ||
      null,
    "bauer-logo-color--glove":
      userChoiceValues["bauer-logo-color--glove"] ||
      userChoiceValues["bauer-logo-color"] ||
      null,
    "flylite-logo-color--glove":
      userChoiceValues["hyp2rlite-logo-color--glove"] ||
      userChoiceValues["hyp2rlite-logo-color"] ||
      null,
    "mach-logo-color--glove":
      userChoiceValues["mach-logo-color--glove"] ||
      userChoiceValues["mach-logo-color"] ||
      null,

    "binding-color--glove":
      userChoiceValues["binding--glove"] || userChoiceValues["binding"] || null,
    "elastic-and-velcro-color--glove":
      userChoiceValues["elastic-and-velcro--glove"] ||
      userChoiceValues["elastic-and-velcro"] ||
      null,
    "construction-lace-color--glove":
      userChoiceValues["construction-lace--glove"] ||
      userChoiceValues["construction-lace"] ||
      null,

    "pocket-lace-color--glove": userChoiceValues["pocket-lace--glove"] || null,

    "t-spine-lace-color--glove":
      userChoiceValues["t-trap"] === "Single"
        ? "N/A"
        : userChoiceValues["t-spine-lace--glove"] || null,

    "middle-zone-color--blocker":
      userChoiceValues["middle-zone-color--blocker"] ||
      userChoiceValues["middle-zone-color"] ||
      null,
    "inside-zone-color--blocker":
      userChoiceValues["inside-zone-color--blocker"] ||
      userChoiceValues["inside-zone-color"] ||
      null,
    "inside-middle-color--blocker":
      userChoiceValues["inside-middle-color--blocker"] ||
      userChoiceValues["inside-middle-color"] ||
      null,
    "inside-middle-accent-color--blocker":
      userChoiceValues["inside-middle-accent-color--blocker"] ||
      userChoiceValues["inside-middle-accent-color"] ||
      null,
    "outside-color--blocker":
      userChoiceValues["outside-zone-color--blocker"] ||
      userChoiceValues["outside-zone-color"] ||
      null,
    "top-color--blocker":
      userChoiceValues["top-zone-color--blocker"] ||
      userChoiceValues["top-zone-color"] ||
      null,

    // Shadow
    "outside-bottom-color--blocker":
      userChoiceValues["outside-bottom-color--blocker"] ||
      userChoiceValues["outside-bottom-zone-color--blocker"] ||
      userChoiceValues["outside-bottom-color"] ||
      userChoiceValues["outside-bottom-zone-color"] ||
      null,
    "outside-top-color--blocker":
      userChoiceValues["outside-top-color--blocker"] ||
      userChoiceValues["outside-top-color"] ||
      null,
    "inside-top-color--blocker":
      userChoiceValues["inside-top-color--blocker"] ||
      userChoiceValues["inside-top-color"] ||
      null,
    "inside-bottom-color--blocker":
      userChoiceValues["inside-bottom-color--blocker"] ||
      userChoiceValues["inside-bottom-color"] ||
      null,
    "shadow-background-color--blocker":
      userChoiceValues["shadow-background-color--blocker"] ||
      userChoiceValues["shadow-background-color"] ||
      null,
    "shadow-logo-fill-color--blocker":
      userChoiceValues["shadow-logo-fill-color--blocker"] ||
      userChoiceValues["shadow-logo-fill-color"] ||
      null,
    "shadow-logo-outline-color--blocker":
      userChoiceValues["shadow-logo-outline-color--blocker"] ||
      userChoiceValues["shadow-logo-outline-color"] ||
      null,

    "line-zone-color--blocker":
      userChoiceValues["line-zone-color--blocker"] ||
      userChoiceValues["line-zone-color"] ||
      null,
    "center-zone-color--blocker":
      userChoiceValues["center-zone-color--blocker"] ||
      userChoiceValues["center-zone-color"] ||
      null,
    "top-spear-color--blocker":
      userChoiceValues["top-spear--blocker"] ||
      userChoiceValues["top-spear"] ||
      null,
    "bottom-spear-color--blocker":
      userChoiceValues["bottom-spear--blocker"] ||
      userChoiceValues["bottom-spear"] ||
      null,
    "spear-color--blocker":
      userChoiceValues["spear-color--blocker"] ||
      userChoiceValues["spear-color"] ||
      null,
    "bauer-logo-color--blocker":
      userChoiceValues["bauer-logo-color--blocker"] ||
      userChoiceValues["bauer-logo-color"] ||
      null,
    "flylite-logo-color--blocker":
      userChoiceValues["hyp2rlite-logo-color--blocker"] ||
      userChoiceValues["hyp2rlite-logo-color"] ||
      null,
    "mach-logo-color--blocker":
      userChoiceValues["mach-logo-color--blocker"] ||
      userChoiceValues["mach-logo-color"] ||
      null,

    "supreme-logo-color--blocker":
      userChoiceValues["supreme-logo-color--blocker"] ||
      userChoiceValues["supreme-logo-color"] ||
      null,

    "binding-color--blocker":
      userChoiceValues["binding--blocker"] ||
      userChoiceValues["binding"] ||
      null,
    "elastic-and-velcro-color--blocker":
      userChoiceValues["elastic-and-velcro--blocker"] ||
      userChoiceValues["elastic-and-velcro"] ||
      null,
    "construction-lace-color--blocker":
      userChoiceValues["construction-lace--blocker"] ||
      userChoiceValues["construction-lace"] ||
      null,
  };

  const digiRetroDesignOverrides = () => {
    if (userChoiceValues["retro-design"] === "reactor-4-chicago") {
      return {
        "center-channel-color--pads": "red",
        "elastic-and-velcro-color--pads": "red",
        "binding-color--pads": "black",
        "calf-wrap-color--pads": "black",

        "elastic-and-velcro-color--glove": "red",
        "binding-color--glove": "black",
        "catcher-back-cover-accent--glove": "red",
        "t-trap-color--glove": "red",

        "elastic-and-velcro-color--blocker": "red",
        "binding-color--blocker": "black",
        "blocker-back-cover-accent--blocker": "red",
      };
    }
    if (userChoiceValues["retro-design"] === "reactor-4-detroit") {
      return {
        "center-channel-color--pads": "red",
        "elastic-and-velcro-color--pads": "red",

        "elastic-and-velcro-color--glove": "red",
        "catcher-back-cover-accent--glove": "red",

        "elastic-and-velcro-color--blocker": "red",
        "blocker-back-cover-accent--blocker": "red",
      };
    }
    if (userChoiceValues["retro-design"] === "reactor-4-los-angeles") {
      return {
        "center-channel-color--pads": "black",
        "elastic-and-velcro-color--pads": "black",

        "elastic-and-velcro-color--glove": "black",
        "catcher-back-cover-accent--glove": "black",

        "elastic-and-velcro-color--blocker": "black",
        "blocker-back-cover-accent--blocker": "black",
      };
    }
    if (userChoiceValues["retro-design"] === "reactor-4-Toronto") {
      return {
        "center-channel-color--pads": "blue",
        "elastic-and-velcro-color--pads": "blue",

        "elastic-and-velcro-color--glove": "blue",
        "catcher-back-cover-accent--glove": "blue",

        "elastic-and-velcro-color--blocker": "blue",
        "blocker-back-cover-accent--blocker": "blue",
      };
    } else return {};
  };

  const handleGloveAndBlockerColors = flow(
    (obj) => ({ ...obj, ...gloveAndBlockerColorChoices }),
    pickBy(negate(isNull))
  );

  const overrideValuesForDigiRetro = flow((obj) => ({
    ...obj,
    ...digiRetroDesignOverrides(),
  }));

  return flow(
    pickBy(negate(isUndefined)),
    mapValues((val) => val.toString().toLowerCase()),
    omit(ignoreValuesFor),
    addRequestedNamingConvention,
    pickBy(negate(isNull)),
    // Rename all full set colors by appending '--pads'
    handleFullSetColors,
    // Add '--glove' and '--blocker' color values, even if same as full set
    handleGloveAndBlockerColors,

    // Override values for specific retro designs
    overrideValuesForDigiRetro,

    // Remove brackets and contents AND kebab-case
    mapValues((val) =>
      val
        .toString()
        .replace(/ *\([^)]*\) */g, "")
        .replace(/[\s-]+/g, "-")
    ),
    mapValues((val) => val.toLowerCase()),
    (userChoices) => ({
      ...userChoices,
      "material-id": materialID,
      ProductFamily: product?.productFamily,
      product: state?.app?.formData?.product,
      // Overwrite personalized name here to ignore "remove brackets and kebab-case"
      ...(userChoiceValues?.personalizedName
        ? {
            "personalized-name":
              userChoiceValues?.personalizedName.toLowerCase(),
          }
        : {}),
    })
  )(userChoiceValues);
}
